import styled from 'styled-components';
import { COLORS } from '../../constants/radar';
import { ROLE_TYPES, ROLE_TYPE_COLORS } from '../../constants/roleTypes';

export const Container = styled.div`
  padding: 24px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${COLORS.BACKGROUND_PRIMARY};
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  background: white;
  padding: 16px 24px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const BackButton = styled.button`
  background: none;
  border: none;
  color: ${COLORS.SECONDARY};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${COLORS.BACKGROUND};
    color: ${COLORS.PRIMARY};
    transform: translateX(-2px);
  }

  svg {
    font-size: 16px;
  }
`;

export const Title = styled.h1`
  color: ${COLORS.PRIMARY};
  font-size: 24px;
  margin: 0;
  font-weight: 600;
  position: relative;
  padding-left: 16px;

  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    height: 24px;
    width: 4px;
    background-color: ${COLORS.PRIMARY};
    border-radius: 2px;
  }
`;

export const Content = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 24px;
  height: calc(100vh - 150px);
  overflow-y: auto;
`;

export const ContentWrapper = styled.div`
  display: flex;
  gap: 24px;
`;

export const ConfigurationSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 200px;
`;

export const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 500;
  text-transform: lowercase;
  margin: 0;
  padding: 12px;
  border: ${({ disabled }) => disabled ? '2px dashed #E0E0E0' : '2px dashed'};
  border-radius: 8px;
  text-align: center;
  color: ${({ type, disabled }) => {
    if (disabled) return '#E0E0E0';
    switch (type) {
      case 'grade':
        return '#00C853';
      case 'position':
        return '#2196F3';
      case ROLE_TYPES.FUNCTIONAL:
        return ROLE_TYPE_COLORS[ROLE_TYPES.FUNCTIONAL].text;
      case ROLE_TYPES.ORGANIZATIONAL:
        return ROLE_TYPE_COLORS[ROLE_TYPES.ORGANIZATIONAL].text;
      case ROLE_TYPES.TECHNICAL:
        return ROLE_TYPE_COLORS[ROLE_TYPES.TECHNICAL].text;
      default:
        return COLORS.SECONDARY;
    }
  }};
  border-color: ${({ type, disabled }) => {
    if (disabled) return '#E0E0E0';
    switch (type) {
      case 'grade':
        return '#00C853';
      case 'position':
        return '#2196F3';
      case ROLE_TYPES.FUNCTIONAL:
        return ROLE_TYPE_COLORS[ROLE_TYPES.FUNCTIONAL].border;
      case ROLE_TYPES.ORGANIZATIONAL:
        return ROLE_TYPE_COLORS[ROLE_TYPES.ORGANIZATIONAL].border;
      case ROLE_TYPES.TECHNICAL:
        return ROLE_TYPE_COLORS[ROLE_TYPES.TECHNICAL].border;
      default:
        return COLORS.BORDER;
    }
  }};
`;

export const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const OptionButton = styled.button`
  padding: 16px;
  border-radius: 12px;
  border: ${({ type, selected }) => {
    if (!selected) return '1px solid #E0E0E0';
    switch (type) {
      case 'grade':
        return '2px solid #00C853';
      case 'position':
        return '2px solid #2196F3';
      case ROLE_TYPES.FUNCTIONAL:
      case ROLE_TYPES.ORGANIZATIONAL:
      case ROLE_TYPES.TECHNICAL:
        return `2px solid ${ROLE_TYPE_COLORS[type].border}`;
      default:
        return '2px solid #E0E0E0';
    }
  }};
  background-color: ${({ type, selected }) => {
    if (!selected) return 'white';
    switch (type) {
      case 'grade':
        return '#E8F5E9';
      case 'position':
        return '#E3F2FD';
      case ROLE_TYPES.FUNCTIONAL:
      case ROLE_TYPES.ORGANIZATIONAL:
      case ROLE_TYPES.TECHNICAL:
        return ROLE_TYPE_COLORS[type].backgroundSelectedLight;
      default:
        return 'white';
    }
  }};
  cursor: pointer;
  font-size: 16px;
  font-weight: ${({ selected }) => (selected ? '600' : '500')};
  text-align: center;
  transition: all 0.2s ease;
  color: ${({ type, selected }) => {
    if (!selected) return '#666';
    switch (type) {
      case 'grade':
        return '#00C853';
      case 'position':
        return '#2196F3';
      case ROLE_TYPES.FUNCTIONAL:
      case ROLE_TYPES.ORGANIZATIONAL:
      case ROLE_TYPES.TECHNICAL:
        return ROLE_TYPE_COLORS[type].border;
      default:
        return '#666';
    }
  }};
  opacity: ${({ selected, disabled }) => {
    if (disabled) return 0.5;
    return 1;
  }};

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const LegendSection = styled.div`
  margin-top: 32px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 32px;

  &:not(:last-child) {
    margin-bottom: 32px;
  }
`;

export const LegendTitle = styled.div`
  font-size: 18px;
  font-weight: 500;
  text-transform: lowercase;
  margin: 0;
  padding: 16px;
  border: 2px dashed;
  border-radius: 8px;
  text-align: center;
  color: ${({ type }) => type === 'grade' ? '#00C853' : '#FF9800'};
  border-color: ${({ type }) => type === 'grade' ? '#00C853' : '#FF9800'};
  margin-bottom: 24px;
`;

export const LegendGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 16px;
  align-items: start;
`;

export const LegendItem = styled.div`
  display: contents;

  & > div:first-child {
    padding: 12px 24px;
    border-radius: 8px;
    border: 1px solid ${({ type }) => type === 'grade' ? '#00C853' : '#FF9800'};
    color: ${({ type }) => type === 'grade' ? '#00C853' : '#FF9800'};
    font-weight: 500;
    text-align: center;
    min-width: 150px;
    height: fit-content;
    align-self: start;
  }

  & > div:last-child {
    padding: 12px 24px;
    border-radius: 8px;
    background: ${({ type }) => type === 'grade' ? '#E8F5E9' : '#FFF3E0'};
    color: #666;
  }
`;

export const HeaderButton = styled.button`
  padding: 8px 16px;
  background-color: white;
  color: ${COLORS.PRIMARY};
  border: 1px solid ${COLORS.PRIMARY};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  white-space: nowrap;
  
  &:hover {
    background-color: ${COLORS.PRIMARY};
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  svg {
    font-size: 16px;
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: scale(1.1);
  }
`;

export const TabContainer = styled.div`
  display: flex;
  gap: 1rem;
  padding: 0 16px;
  margin-bottom: 0;
  background-color: ${COLORS.BACKGROUND_PRIMARY};
`;

export const TabButton = styled.button`
  padding: 12px 24px;
  border: none;
  background: none;
  cursor: pointer;
  color: ${({ active }) => active ? COLORS.PRIMARY : '#94A3B8'};
  transition: all 0.2s ease;
  font-weight: ${({ active }) => active ? '600' : '400'};
  font-size: 16px;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: ${({ active }) => active ? '100%' : '0'};
    height: 3px;
    background-color: ${COLORS.PRIMARY};
    transition: width 0.2s ease;
  }

  &:hover {
    color: ${COLORS.PRIMARY};
  }

  &:focus {
    outline: none;
  }
`;

export const ProjectsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px;
`;

export const ProjectBadge = styled.button`
  padding: 6px 16px;
  border-radius: 20px;
  border: 1px solid ${({ selected }) => selected ? COLORS.PRIMARY : COLORS.BACKGROUND};
  background-color: ${({ selected }) => selected ? `${COLORS.PRIMARY}10` : 'white'};
  color: ${({ selected }) => selected ? COLORS.PRIMARY : '#94A3B8'};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 15px;
  font-weight: ${({ selected }) => selected ? '600' : '400'};
  line-height: 1.2;
  height: min-content;

  &:hover {
    border-color: ${COLORS.PRIMARY};
    color: ${COLORS.PRIMARY};
    background-color: ${`${COLORS.PRIMARY}10`};
  }
`;

export const SearchContainer = styled.div`
  position: relative;
  width: 300px;
  z-index: 1;
  display: flex;
  align-items: center;
  margin-bottom: 24px;
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: 8px 40px 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 12px;
  outline: none;
  position: relative;
  
  &:focus {
    border-color: #666;
    box-shadow: 0 0 0 2px rgba(0,0,0,0.1);
  }

  &::placeholder {
    color: #94A3B8;
  }
`;

export const SearchIcon = styled.div`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94A3B8;
  width: 24px;
  height: 24px;
  pointer-events: none;
  opacity: 0.5;
`; 